/**
 * Decisions & Incidents. On a phone this is the screen that matters most, so it
 * is a single-column priority feed rather than a table.
 */
import { api } from '../lib/api';
import { useProjection } from '../hooks/useProjection';
import { useLive } from '../hooks/useLive';
import { Intervention } from '../components/Intervention';
import { Panel } from '../components/Primitives';
import { clockTime } from '../lib/format';
import {
  DegradedBanner,
  EmptyState,
  ErrorState,
  LiveBadge,
  SkeletonPanel,
} from '../components/States';

const GROUPS = [
  {
    key: 'confirmed',
    title: 'Confirmed failures and blockades',
    body: 'Something is genuinely broken or stopped. These are the only items that are red.',
    match: (severity: string) => severity === 'CRITICAL' || severity === 'BLOCKER',
  },
  {
    key: 'rising',
    title: 'Rising risk',
    body: 'Not broken yet, but heading that way without intervention.',
    match: (severity: string) => severity === 'RISK',
  },
  {
    key: 'attention',
    title: 'Attention and stale data',
    body: 'Uncertainty rather than failure: an ageing source, or a fact DCC can no longer vouch for.',
    match: (severity: string) => severity === 'ATTENTION' || severity === 'INFO',
  },
];

export function Alerts() {
  const projection = useProjection(() => api.portfolio(), []);
  const live = useLive(
    ['incident.updated', 'portfolio.updated', 'lease.updated'],
    projection.reload,
  );

  if (projection.loading) return <SkeletonPanel rows={5} />;
  if (projection.error) return <ErrorState error={projection.error} onRetry={projection.reload} />;
  const view = projection.data!;

  return (
    <>
      <header className="topstrip">
        <div>
          <div className="label">{view.tenant.name}</div>
          <h1 className="topstrip__title">Decisions &amp; incidents</h1>
        </div>
        <div className="topstrip__meta">
          <LiveBadge connection={live.connection} lastEventAt={live.lastEventAt} />
        </div>
      </header>

      {projection.degraded && (
        <DegradedBanner onRetry={projection.reload} loadedAt={projection.loadedAt} />
      )}

      {view.interventions.length === 0 && (
        <EmptyState
          tone="success"
          title="Nothing needs a person right now"
          body="No confirmed failure, blockade, overdue decision, lease exception or degraded connection is open."
        />
      )}

      {GROUPS.map((group) => {
        const items = view.interventions.filter((item) => group.match(item.severity));
        if (items.length === 0) return null;
        return (
          <Panel key={group.key} title={`${group.title} (${items.length})`} subtitle={group.body}>
            <div className="stack stack--tight">
              {items.map((item) => (
                <Intervention key={item.id} item={item} />
              ))}
            </div>
          </Panel>
        );
      })}

      <Panel
        title="What happens if nobody intervenes"
        subtitle="Derived from the open items above and the current forecasts."
      >
        <ul className="stack stack--tight small" style={{ margin: 0, paddingLeft: 18 }}>
          {view.projects
            .filter(
              (tile) =>
                tile.status === 'BLOCKED' ||
                tile.status === 'AT_RISK' ||
                tile.status === 'WAITING_DECISION',
            )
            .map((tile) => (
              <li key={tile.id}>
                <strong>{tile.name}</strong> — {tile.statusReason}{' '}
                {tile.forecast.etaAt
                  ? `The current forecast already includes this; finishing moves out with every hour it stays open.`
                  : 'No finish time can be derived while this is open.'}
              </li>
            ))}
          {view.crew
            .filter((member) => member.status === 'BLOCKED_LIMIT')
            .map((member) => (
              <li key={member.id}>
                <strong>{member.name}</strong> stays stopped until{' '}
                {member.sessionRange.resetsAt
                  ? clockTime(member.sessionRange.resetsAt)
                  : 'the window resets'}
                , unless the work is handed to another authorised colleague.
              </li>
            ))}
          {view.projects.every((t) => t.status === 'ACTIVE' || t.status === 'COMPLETED') &&
            view.crew.every((c) => c.status !== 'BLOCKED_LIMIT') && (
              <li>Nothing degrades on its own from the currently open items.</li>
            )}
        </ul>
      </Panel>
    </>
  );
}
